import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { grabsApi } from "@/api/grabs";
import { tvApi } from "@/api/tv";
import {
  normalizeSeriesStatus,
  type Episode,
  type GrabRecord,
  type GrabStatus,
  type Season,
  type SeriesStatus,
  type SeriesType,
  type SeriesWithSeasons,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status, type StatusTone } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { RedownloadButton } from "@/components/RedownloadButton";
import { SubtitleSearchModal } from "@/components/SubtitleSearchModal";
import { TagPills } from "@/components/TagPills";
import { cn } from "@/lib/cn";
import {
  airsLabel,
  isAired,
  isTbaTitle,
  SERIES_TYPE_LABEL,
  SERIES_TYPE_OPTIONS,
} from "@/lib/tv";

/**
 * Series detail page (`/tv/$id`). Mirrors MovieDetail's editorial header
 * but adds the seasons → episodes hierarchy the user wanted: each season
 * gets its own collapsible section showing which episodes have files,
 * which are wanted, and which are still upcoming.
 *
 * Per-episode optimistic UI matches MoviesWanted / TVWanted: idle →
 * searching → grabbed/failed transitions live in row state and don't
 * wait for a refetch.
 */

const grabStatusTone: Record<GrabStatus, StatusTone> = {
  queued: "info",
  downloading: "info",
  completed: "good",
  imported: "good",
  failed: "bad",
  removed: "neutral",
};

type RowState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "grabbed"; record: GrabRecord }
  | { kind: "failed"; reason: string };

export function SeriesDetail() {
  const { id } = useParams({ from: "/tv/$id" });
  const seriesId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const seriesQ = useQuery({
    queryKey: ["series", seriesId],
    queryFn: () => tvApi.get(seriesId),
  });
  const episodesQ = useQuery({
    queryKey: ["series", seriesId, "episodes"],
    queryFn: () => tvApi.episodes(seriesId),
  });
  const grabsQ = useQuery({
    queryKey: ["grabs", "episode", "series", seriesId],
    // Filter on the client side: we want every grab whose episode belongs
    // to this series. The grabs API filters by media_id which is the
    // episode id, not the series id, so a single per-series fetch isn't
    // available. Pull a generous limit and filter post-hoc — these lists
    // are small in practice.
    queryFn: () => grabsApi.list({ media_kind: "episode", limit: 200 }),
  });

  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});
  const [searchingAll, setSearchingAll] = useState(false);
  // Per-episode subtitle picker (#87). Tracks which episode's modal is
  // open; null = no modal. Defined at this level (rather than per-
  // EpisodeRow) so React doesn't re-mount the modal on every parent
  // re-render.
  const [subtitlesFor, setSubtitlesFor] = useState<Episode | null>(null);
  // Per-episode rename in-flight tracker (#70). Local because the
  // mutation lives in `tvApi.renameEpisode` and we want each row's
  // button to reflect its own pending state, not the whole series.
  const [renamingEpisode, setRenamingEpisode] = useState<Set<number>>(
    () => new Set(),
  );
  const [renamingAll, setRenamingAll] = useState(false);

  const monitorSeriesMut = useMutation({
    mutationFn: (monitored: boolean) =>
      tvApi.update(seriesId, { monitored }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["series", seriesId] });
      qc.invalidateQueries({ queryKey: ["series"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  // Issue #65: change `series_type` via the chip dropdown.
  const updateTypeMut = useMutation({
    mutationFn: (series_type: SeriesType) =>
      tvApi.update(seriesId, { series_type }),
    onSuccess: (s) => {
      toast(`Type → ${SERIES_TYPE_LABEL[s.series_type]}`, "success");
      qc.invalidateQueries({ queryKey: ["series", seriesId] });
      qc.invalidateQueries({ queryKey: ["series"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Type update failed", "error"),
  });

  // Issue #66: per-series "monitor specials" override. PATCH writes
  // through to the JSON-blob settings; the server echoes the resolved
  // value on the next series GET so we don't have to maintain a local
  // shadow.
  const monitorSpecialsMut = useMutation({
    mutationFn: (monitor_specials: boolean) =>
      tvApi.update(seriesId, { monitor_specials }),
    onSuccess: (_s, monitor_specials) => {
      toast(
        monitor_specials
          ? "Now monitoring specials (S00) for this series"
          : "Specials (S00) hidden for this series",
        "success",
      );
      qc.invalidateQueries({ queryKey: ["series", seriesId] });
      qc.invalidateQueries({ queryKey: ["series", seriesId, "episodes"] });
      qc.invalidateQueries({ queryKey: ["tv", "wanted"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const removeMut = useMutation({
    mutationFn: () => tvApi.remove(seriesId),
    onSuccess: () => {
      toast("Series removed", "success");
      qc.invalidateQueries({ queryKey: ["series"] });
      navigate({ to: "/tv" });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const monitorEpisodeMut = useMutation({
    mutationFn: ({ id, monitored }: { id: number; monitored: boolean }) =>
      tvApi.updateEpisode(id, { monitored }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["series", seriesId, "episodes"] });
      qc.invalidateQueries({ queryKey: ["tv", "wanted"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  // Season-level monitor toggle (#67). Optimistic: flip the season's
  // `monitored` flag and apply the rollup to every episode in the
  // season immediately so the icon and per-episode bookmarks settle
  // without waiting for the network. On error we restore the snapshots.
  const seriesQueryKey = ["series", seriesId] as const;
  const episodesQueryKey = ["series", seriesId, "episodes"] as const;

  const monitorSeasonMut = useMutation({
    mutationFn: ({ id, monitored }: { id: number; monitored: boolean }) =>
      tvApi.updateSeason(id, { monitored }),
    onMutate: async ({ id, monitored }) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: seriesQueryKey }),
        qc.cancelQueries({ queryKey: episodesQueryKey }),
      ]);
      const prevSeries = qc.getQueryData<SeriesWithSeasons>(seriesQueryKey);
      const prevEpisodes = qc.getQueryData<Episode[]>(episodesQueryKey);
      if (prevSeries) {
        qc.setQueryData<SeriesWithSeasons>(seriesQueryKey, {
          ...prevSeries,
          seasons: prevSeries.seasons.map((s) =>
            s.id === id ? { ...s, monitored } : s,
          ),
        });
      }
      if (prevEpisodes) {
        qc.setQueryData<Episode[]>(
          episodesQueryKey,
          prevEpisodes.map((e) =>
            e.season_id === id ? { ...e, monitored } : e,
          ),
        );
      }
      return { prevSeries, prevEpisodes };
    },
    onError: (e, _vars, ctx) => {
      if (ctx?.prevSeries) qc.setQueryData(seriesQueryKey, ctx.prevSeries);
      if (ctx?.prevEpisodes)
        qc.setQueryData(episodesQueryKey, ctx.prevEpisodes);
      toast(e instanceof Error ? e.message : "Update failed", "error");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: seriesQueryKey });
      qc.invalidateQueries({ queryKey: episodesQueryKey });
      qc.invalidateQueries({ queryKey: ["tv", "wanted"] });
    },
  });

  const grabEpisode = (ep: Episode) => {
    setRowStates((s) => ({ ...s, [ep.id]: { kind: "searching" } }));
    tvApi
      .searchEpisode(ep.id)
      .then((rec) => {
        setRowStates((s) => ({
          ...s,
          [ep.id]: { kind: "grabbed", record: rec },
        }));
        toast(
          `Grabbed ${rec.release_title} via ${rec.indexer_name ?? "?"} → ${rec.download_client_name ?? "?"}`,
          "success",
        );
        qc.invalidateQueries({ queryKey: ["grabs"] });
        qc.invalidateQueries({
          queryKey: ["grabs", "episode", "series", seriesId],
        });
      })
      .catch((err) => {
        const reason = err instanceof Error ? err.message : "Search failed";
        setRowStates((s) => ({ ...s, [ep.id]: { kind: "failed", reason } }));
        toast(reason, "error");
      });
  };

  const searchAllMissing = async () => {
    const missing = (episodesQ.data ?? []).filter(
      (e) => !e.has_file && e.monitored && isAired(e.air_date),
    );
    if (missing.length === 0) {
      toast("Nothing missing to search", "info");
      return;
    }
    if (
      !confirm(
        `Search ${missing.length} missing episode${missing.length === 1 ? "" : "s"} across your indexers?`,
      )
    ) {
      return;
    }
    setSearchingAll(true);
    let succeeded = 0;
    let failed = 0;
    const CONCURRENCY = 3;
    const queue = missing.slice();
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length > 0) {
        const ep = queue.shift();
        if (!ep) return;
        setRowStates((s) => ({ ...s, [ep.id]: { kind: "searching" } }));
        try {
          const rec = await tvApi.searchEpisode(ep.id);
          setRowStates((s) => ({
            ...s,
            [ep.id]: { kind: "grabbed", record: rec },
          }));
          succeeded += 1;
        } catch (err) {
          const reason = err instanceof Error ? err.message : "failed";
          setRowStates((s) => ({
            ...s,
            [ep.id]: { kind: "failed", reason },
          }));
          failed += 1;
        }
      }
    });
    await Promise.all(workers);
    setSearchingAll(false);
    qc.invalidateQueries({ queryKey: ["series", seriesId, "episodes"] });
    qc.invalidateQueries({ queryKey: ["grabs"] });
    toast(
      `Searched ${missing.length} — ${succeeded} grabbed, ${failed} no match / failed`,
      succeeded > 0 ? "success" : "info",
    );
  };

  // Rename the on-disk file for one episode to the canonical
  // `{Series} - SxxEyy - {Title}.{ext}` form (#70). Useful after a
  // grab cancellation that left the file under the old release name.
  const renameEpisode = async (ep: Episode) => {
    setRenamingEpisode((set) => {
      const next = new Set(set);
      next.add(ep.id);
      return next;
    });
    try {
      const res = await tvApi.renameEpisode(ep.id);
      if (res.status === "renamed") {
        toast(`Renamed S${pad2(ep.season_number)}E${pad2(ep.episode_number)}`, "success");
        qc.invalidateQueries({ queryKey: ["series", seriesId, "episodes"] });
      } else {
        toast(
          `S${pad2(ep.season_number)}E${pad2(ep.episode_number)} already at canonical filename`,
          "info",
        );
      }
    } catch (err) {
      const reason = err instanceof Error ? err.message : "Rename failed";
      toast(reason, "error");
    } finally {
      setRenamingEpisode((set) => {
        const next = new Set(set);
        next.delete(ep.id);
        return next;
      });
    }
  };

  // Series-level "rename all". Bounded concurrency lives on the
  // backend so we just fire one POST and surface the summary.
  const renameAll = async () => {
    const filed = (episodesQ.data ?? []).filter((e) => e.has_file).length;
    if (filed === 0) {
      toast("No filed episodes to rename", "info");
      return;
    }
    if (
      !confirm(
        `Rename ${filed} episode${filed === 1 ? "" : "s"} to the canonical filename? Files already at the canonical name are skipped.`,
      )
    ) {
      return;
    }
    setRenamingAll(true);
    try {
      const res = await tvApi.renameSeries(seriesId);
      qc.invalidateQueries({ queryKey: ["series", seriesId, "episodes"] });
      const tone = res.failed > 0 ? "error" : res.renamed > 0 ? "success" : "info";
      toast(
        `Rename: ${res.renamed} renamed, ${res.unchanged} already canonical${
          res.failed > 0 ? `, ${res.failed} failed` : ""
        }`,
        tone,
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : "Rename failed", "error");
    } finally {
      setRenamingAll(false);
    }
  };

  // Group episodes by season number; sort each season's episodes by
  // episode_number; sort seasons descending (newest first) — viewers
  // typically want "what's the most recent season?" answered fast.
  // Pair each grouping with its `seasons` row so the season-level
  // monitor toggle has the season id (PATCH /api/v1/seasons/:id).
  const seasonsView = useMemo(() => {
    const eps = episodesQ.data ?? [];
    const seasonsById = new Map<number, Season>();
    for (const s of seriesQ.data?.seasons ?? []) {
      seasonsById.set(s.season_number, s);
    }
    const bySeason = new Map<number, Episode[]>();
    for (const ep of eps) {
      const arr = bySeason.get(ep.season_number) ?? [];
      arr.push(ep);
      bySeason.set(ep.season_number, arr);
    }
    return Array.from(bySeason.entries())
      .map(([seasonNumber, episodes]) => ({
        seasonNumber,
        season: seasonsById.get(seasonNumber),
        episodes: episodes
          .slice()
          .sort((a, b) => a.episode_number - b.episode_number),
      }))
      .sort((a, b) => b.seasonNumber - a.seasonNumber);
  }, [episodesQ.data, seriesQ.data]);

  // Filter the broad grabs result down to this series' episodes for the
  // history table at the bottom.
  const seriesGrabs = useMemo(() => {
    const epIds = new Set((episodesQ.data ?? []).map((e) => e.id));
    return (grabsQ.data ?? []).filter((g) => epIds.has(g.media_id));
  }, [grabsQ.data, episodesQ.data]);

  if (seriesQ.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton.Block height="3rem" width="22rem" />
        <div className="flex flex-col gap-6 sm:grid sm:grid-cols-[14rem_1fr] sm:gap-8">
          <Skeleton.Poster />
          <Skeleton.Text lines={5} />
        </div>
      </div>
    );
  }
  if (!seriesQ.data) {
    return <p className="text-sm text-foreground/55">Not found.</p>;
  }

  const series = seriesQ.data;
  const status = normalizeSeriesStatus(series.status);
  const allEpisodes = episodesQ.data ?? [];
  const monitoredCount = allEpisodes.filter((e) => e.monitored).length;
  const filedCount = allEpisodes.filter((e) => e.has_file).length;
  const missingCount = allEpisodes.filter(
    (e) => !e.has_file && e.monitored && isAired(e.air_date),
  ).length;

  return (
    <div className="space-y-8">
      <div className="text-xs">
        <Link
          to="/tv"
          className="text-foreground/55 transition hover:text-accent"
        >
          ← All series
        </Link>
      </div>

      <PageHeader
        eyebrow="Series"
        title={series.title}
        description={series.overview ?? undefined}
        actions={
          <>
            <Button
              variant="secondary"
              loading={monitorSeriesMut.isPending}
              onClick={() => monitorSeriesMut.mutate(!series.monitored)}
            >
              {series.monitored ? "Unmonitor" : "Monitor"}
            </Button>
            <Button
              loading={searchingAll}
              disabled={missingCount === 0}
              onClick={searchAllMissing}
            >
              {searchingAll
                ? "Searching…"
                : missingCount > 0
                  ? `Search ${missingCount} missing`
                  : "All episodes filed"}
            </Button>
            <Button
              variant="secondary"
              loading={renamingAll}
              disabled={filedCount === 0}
              onClick={renameAll}
              title="Rename every filed episode to the canonical {Series} - SxxEyy - {Title}.{ext} form."
            >
              {renamingAll
                ? "Renaming…"
                : filedCount > 0
                  ? `Rename ${filedCount} filed`
                  : "Nothing to rename"}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (
                  confirm(
                    `Remove "${series.title}" from your library? Files on disk are kept.`,
                  )
                )
                  removeMut.mutate();
              }}
            >
              Remove
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-6 sm:grid sm:grid-cols-[14rem_1fr] sm:gap-8">
        {series.poster_url ? (
          <img
            src={series.poster_url}
            alt=""
            className="aspect-[2/3] w-40 self-center rounded-md border border-line object-cover shadow-elev sm:w-56 sm:self-auto"
          />
        ) : (
          <div className="grid aspect-[2/3] w-40 self-center place-items-center rounded-md border border-line bg-surface text-xs text-foreground/35 sm:w-56 sm:self-auto">
            no poster
          </div>
        )}
        <div className="space-y-6 text-sm">
          <div className="flex flex-wrap gap-2">
            <SeriesStatusChip status={status} />
            {series.first_air_date && (
              <Status variant="chip" tone="neutral">
                {series.first_air_date.slice(0, 4)}
              </Status>
            )}
            <Status variant="chip" tone={series.monitored ? "accent" : "neutral"}>
              {series.monitored ? "monitored" : "unmonitored"}
            </Status>
            <SeriesTypeChip
              value={series.series_type}
              pending={updateTypeMut.isPending}
              onChange={(t) => updateTypeMut.mutate(t)}
            />
          </div>

          <TagPills
            target={{ kind: "media", mediaKind: "series", id: series.id }}
            showEmpty
          />

          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <SummaryStat label="Seasons" value={series.seasons.length} />
            <SummaryStat
              label="Episodes"
              value={`${monitoredCount} / ${allEpisodes.length}`}
              hint="monitored"
            />
            <SummaryStat
              label="Filed"
              value={`${filedCount} / ${allEpisodes.length}`}
              tone={
                filedCount === allEpisodes.length && allEpisodes.length > 0
                  ? "good"
                  : filedCount > 0
                    ? "neutral"
                    : "neutral"
              }
            />
          </div>

          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-xs">
            <dt className="text-foreground/45">TMDB</dt>
            <dd className="font-mono text-foreground/85">
              {series.tmdb_id ?? "—"}
            </dd>
            <dt className="text-foreground/45">IMDB</dt>
            <dd className="font-mono text-foreground/85">
              {series.imdb_id ?? "—"}
            </dd>
            <dt className="text-foreground/45">Path</dt>
            <dd className="font-mono break-all text-foreground/85">
              {series.path ?? "—"}
            </dd>
          </dl>

          <SpecialsToggleRow
            value={series.monitor_specials}
            pending={monitorSpecialsMut.isPending}
            onChange={(v) => monitorSpecialsMut.mutate(v)}
          />
        </div>
      </div>

      <section className="space-y-4">
        <h2 className="eyebrow">Seasons</h2>
        {episodesQ.isLoading ? (
          <Skeleton.Rows count={8} />
        ) : seasonsView.length === 0 ? (
          <EmptyState
            glyph="antenna"
            title="No episodes yet"
            description="TMDB hasn't returned an episode list for this series yet. Try the metadata refresh on /settings/tv."
          />
        ) : (
          <>
            {seasonsView.map((s) => (
              <SeasonSection
                key={s.seasonNumber}
                seasonNumber={s.seasonNumber}
                season={s.season}
                episodes={s.episodes}
                rowStates={rowStates}
                renamingEpisode={renamingEpisode}
                onGrab={grabEpisode}
                onRename={renameEpisode}
                onMonitorEpisode={(id, monitored) =>
                  monitorEpisodeMut.mutate({ id, monitored })
                }
                onMonitorSeason={(id, monitored) =>
                  monitorSeasonMut.mutate({ id, monitored })
                }
                onSubtitles={(ep) => setSubtitlesFor(ep)}
              />
            ))}
            <p className="font-mono text-[11px] text-foreground/45">
              {formatEpisodeRefreshFooter(series.last_episode_refresh_at)}
            </p>
          </>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="eyebrow">Recent grabs</h2>
        {grabsQ.isLoading ? (
          <Skeleton.Rows count={4} />
        ) : seriesGrabs.length === 0 ? (
          <EmptyState
            glyph="search"
            title="No grabs yet"
            description="Click Search on any wanted episode above to query indexers."
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
            <table className="w-full text-xs">
              <thead className="bg-surface-2/50">
                <tr className="text-left">
                  {["Release", "Indexer", "Client", "Status", "When", ""].map(
                    (h, i) => (
                      <th
                        key={h || `col-${i}`}
                        className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {seriesGrabs.slice(0, 30).map((g) => (
                  <tr key={g.id} className="transition hover:bg-surface-2/40">
                    <td className="max-w-md truncate px-3 py-2.5 font-mono text-[11px]">
                      {g.release_title}
                    </td>
                    <td className="px-3 py-2.5 text-foreground/70">
                      {g.indexer_name ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-foreground/70">
                      {g.download_client_name ?? "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <Status
                        variant="chip"
                        tone={grabStatusTone[g.status]}
                        pulse={g.status === "downloading"}
                      >
                        {g.status}
                      </Status>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-foreground/55">
                      {new Date(g.grabbed_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <RedownloadButton
                        grabId={g.id}
                        releaseTitle={g.release_title}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {subtitlesFor && (
        <SubtitleSearchModal
          open={subtitlesFor !== null}
          onClose={() => setSubtitlesFor(null)}
          mediaKind="episode"
          mediaId={subtitlesFor.id}
          mediaTitle={`${series.title} · S${String(subtitlesFor.season_number).padStart(2, "0")}E${String(subtitlesFor.episode_number).padStart(2, "0")} — ${subtitlesFor.title}`}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Season section
// ---------------------------------------------------------------------------

function SeasonSection({
  seasonNumber,
  season,
  episodes,
  rowStates,
  renamingEpisode,
  onGrab,
  onRename,
  onMonitorEpisode,
  onMonitorSeason,
  onSubtitles,
}: {
  seasonNumber: number;
  /**
   * The `seasons` row from `series.seasons`, when present. Carries the
   * id we need for `PATCH /api/v1/seasons/:id`. Optional because in
   * theory episodes can exist for a season that the seasons list
   * hasn't caught up to yet — we hide the toggle in that case.
   */
  season: Season | undefined;
  episodes: Episode[];
  rowStates: Record<number, RowState>;
  /** Set of episode ids whose `tvApi.renameEpisode` request is pending (#70). */
  renamingEpisode: Set<number>;
  onGrab: (ep: Episode) => void;
  onRename: (ep: Episode) => void;
  onMonitorEpisode: (id: number, monitored: boolean) => void;
  onMonitorSeason: (id: number, monitored: boolean) => void;
  /** Open the manual subtitle picker for this episode. Surfaced only
   *  when the episode has a file on disk (a sub without a video to
   *  attach to is meaningless). */
  onSubtitles: (ep: Episode) => void;
}) {
  // Default to expanded; collapse Season 0 (specials) by default.
  const [expanded, setExpanded] = useState(seasonNumber !== 0);

  const filed = episodes.filter((e) => e.has_file).length;
  const monitoredCount = episodes.filter((e) => e.monitored).length;
  const missing = episodes.filter(
    (e) => !e.has_file && e.monitored && isAired(e.air_date),
  ).length;

  // Tristate icon reflects the actual rollup state of this season's
  // episodes. We drive the click off this — clicking when "all" sends
  // monitored=false; otherwise sends monitored=true (which will set
  // every episode back on, per the v1 rollup spec).
  const allMonitored =
    episodes.length > 0 && monitoredCount === episodes.length;
  const noneMonitored = monitoredCount === 0;
  const rollup: "all" | "none" | "mixed" = allMonitored
    ? "all"
    : noneMonitored
      ? "none"
      : "mixed";

  const label =
    seasonNumber === 0 ? "Specials" : `Season ${seasonNumber}`;

  return (
    <div className="overflow-hidden rounded-md border border-line bg-surface/30">
      <div className="flex items-stretch bg-surface-2/40">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="focus-ring flex flex-1 items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-surface-2/60"
        >
          <div className="flex items-center gap-3">
            <svg
              className={cn(
                "h-3 w-3 text-foreground/55 transition-transform",
                expanded && "rotate-90",
              )}
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              aria-hidden
            >
              <path
                d="m4.5 3 3 3-3 3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="font-display text-base font-medium tracking-display-tight">
              {label}
            </span>
            <span className="font-mono text-[11px] text-foreground/55">
              {episodes.length} ep{episodes.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {filed > 0 && (
              <Status variant="chip" tone="good">
                {filed} filed
              </Status>
            )}
            {missing > 0 && (
              <Status variant="chip" tone="accent">
                {missing} wanted
              </Status>
            )}
            {monitoredCount < episodes.length && (
              <Status variant="chip" tone="neutral">
                {episodes.length - monitoredCount} unmonitored
              </Status>
            )}
          </div>
        </button>
        {season && (
          <button
            type="button"
            onClick={(e) => {
              // Don't bubble into the expand/collapse handler.
              e.stopPropagation();
              // "mixed" → re-arm everything; "all" → unmonitor; "none" → monitor.
              onMonitorSeason(season.id, rollup !== "all");
            }}
            className="focus-ring flex items-center px-4 text-foreground/55 transition hover:bg-surface-2/60 hover:text-foreground"
            title={
              rollup === "all"
                ? "Unmonitor entire season"
                : rollup === "none"
                  ? "Monitor entire season"
                  : "Re-monitor entire season (some episodes are unmonitored)"
            }
            aria-label={
              rollup === "all"
                ? "Unmonitor season"
                : rollup === "none"
                  ? "Monitor season"
                  : "Re-monitor season"
            }
          >
            <SeasonMonitorGlyph rollup={rollup} />
          </button>
        )}
      </div>

      {expanded && (
        <table className="w-full text-sm">
          <thead className="bg-surface/40">
            <tr className="text-left">
              <th className="w-12 px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                #
              </th>
              <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Title
              </th>
              <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Air date
              </th>
              <th className="px-3 py-2 text-right font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Runtime
              </th>
              <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Status
              </th>
              <th className="w-32 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {episodes.map((ep) => (
              <EpisodeRow
                key={ep.id}
                ep={ep}
                state={rowStates[ep.id] ?? { kind: "idle" }}
                renaming={renamingEpisode.has(ep.id)}
                onGrab={() => onGrab(ep)}
                onRename={() => onRename(ep)}
                onToggleMonitor={() =>
                  onMonitorEpisode(ep.id, !ep.monitored)
                }
                onSubtitles={() => onSubtitles(ep)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EpisodeRow({
  ep,
  state,
  renaming,
  onGrab,
  onRename,
  onToggleMonitor,
  onSubtitles,
}: {
  ep: Episode;
  state: RowState;
  /** Per-episode rename request in flight (#70). */
  renaming: boolean;
  onGrab: () => void;
  onRename: () => void;
  onToggleMonitor: () => void;
  onSubtitles: () => void;
}) {
  const dimmed = ep.has_file && !rowStateIsActive(state);
  const aired = isAired(ep.air_date);

  return (
    <tr
      className={cn(
        "group transition hover:bg-surface-2/40",
        dimmed && "opacity-65",
      )}
    >
      <td className="px-3 py-2 font-mono text-xs tabular-nums text-foreground/65">
        {String(ep.episode_number).padStart(2, "0")}
      </td>
      <td className="px-3 py-2.5">
        {isTbaTitle(ep.title) ? (
          <span className="font-medium text-foreground/45 italic">
            Title TBD
          </span>
        ) : (
          <span className="font-medium">{ep.title}</span>
        )}
        {ep.overview && (
          <div
            className="mt-0.5 line-clamp-1 max-w-2xl text-xs text-foreground/55"
            title={ep.overview}
          >
            {ep.overview}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 font-mono text-xs text-foreground/65">
        {ep.air_date ?? "—"}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-foreground/65">
        {ep.runtime_minutes ? `${ep.runtime_minutes}m` : "—"}
      </td>
      <td className="px-3 py-2.5">
        <EpisodeStatusChip ep={ep} state={state} aired={aired} />
      </td>
      <td className="space-x-1.5 px-3 py-2 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={onToggleMonitor}
          className="focus-ring rounded-md p-1.5 text-foreground/45 transition hover:bg-surface-2 hover:text-foreground"
          title={ep.monitored ? "Unmonitor episode" : "Monitor episode"}
          aria-label={ep.monitored ? "Unmonitor" : "Monitor"}
        >
          <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 16 16"
            fill={ep.monitored ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth={1.4}
            aria-hidden
          >
            <path d="M4 2v12l4-3 4 3V2z" strokeLinejoin="round" />
          </svg>
        </button>
        {ep.has_file && (
          <>
            {/* Issue #87: per-episode manual subtitle picker. Only shows
             * when the episode has a file on disk — there's no video to
             * attach a subtitle to otherwise. */}
            <button
              type="button"
              onClick={onSubtitles}
              className="focus-ring rounded-md px-1.5 py-1.5 font-mono text-[10px] font-medium text-foreground/45 transition hover:bg-surface-2 hover:text-foreground"
              title="Search subtitles"
              aria-label="Search subtitles"
            >
              CC
            </button>
            {/* Per-episode rename (#70). Brings the file back to the
             * canonical `{Series} - SxxEyy - {Title}.{ext}` form when a
             * grab was cancelled and re-grabbed under a different
             * release name. */}
            <Button
              size="sm"
              variant="secondary"
              loading={renaming}
              onClick={onRename}
              title="Rename file to canonical {Series} - SxxEyy - {Title}.{ext}"
            >
              Rename
            </Button>
          </>
        )}
        {!ep.has_file && aired && state.kind !== "grabbed" && (
          <Button
            size="sm"
            variant="secondary"
            loading={state.kind === "searching"}
            onClick={onGrab}
          >
            {state.kind === "failed" ? "Retry" : "Search"}
          </Button>
        )}
        {!ep.has_file && !aired && state.kind !== "grabbed" && (
          // Issue #64: per-episode air-date gate. Don't surface the
          // Search button for episodes that haven't aired yet — show
          // the relative `Airs in 3 days` / `Airs Apr 30` label instead
          // so the user knows why we're not searching. (Manual search
          // still works via the row menu / direct API call — this just
          // de-emphasises it in the common case.)
          <span
            className="font-mono text-[10px] text-foreground/45 italic"
            title={ep.air_date ?? undefined}
          >
            {airsLabel(ep.air_date) ?? "Upcoming"}
          </span>
        )}
        {state.kind === "grabbed" && (
          <span className="font-mono text-[10px] text-foreground/45">
            {state.record.indexer_name ?? "?"}
          </span>
        )}
      </td>
    </tr>
  );
}

function EpisodeStatusChip({
  ep,
  state,
  aired,
}: {
  ep: Episode;
  state: RowState;
  aired: boolean;
}) {
  if (state.kind === "searching") {
    return (
      <Status variant="chip" tone="info" pulse>
        searching
      </Status>
    );
  }
  if (state.kind === "grabbed") {
    return (
      <Status variant="chip" tone="good">
        grabbed
      </Status>
    );
  }
  if (state.kind === "failed") {
    return (
      <Status variant="chip" tone="bad">
        {state.reason.length > 18 ? "failed" : state.reason}
      </Status>
    );
  }
  if (ep.has_file) {
    return (
      <Status variant="chip" tone="good">
        filed
      </Status>
    );
  }
  if (!ep.monitored) {
    return (
      <Status variant="chip" tone="neutral">
        unmonitored
      </Status>
    );
  }
  if (!aired) {
    return (
      <Status variant="chip" tone="info">
        upcoming
      </Status>
    );
  }
  return (
    <Status variant="chip" tone="accent">
      wanted
    </Status>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Tristate bookmark glyph for the season-level monitor toggle.
 *
 * - `all`   → filled bookmark (every episode monitored)
 * - `none`  → hollow bookmark
 * - `mixed` → bookmark with a small dot, signalling partial state. The
 *   click action re-arms the whole season, matching the v1 rollup
 *   behaviour described in #67.
 */
function SeasonMonitorGlyph({
  rollup,
}: {
  rollup: "all" | "none" | "mixed";
}) {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 16 16"
      fill={rollup === "all" ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1.4}
      aria-hidden
    >
      <path d="M4 2v12l4-3 4 3V2z" strokeLinejoin="round" />
      {rollup === "mixed" && (
        // Filled inner dot indicating "some, not all".
        <circle cx="8" cy="7" r="1.4" fill="currentColor" stroke="none" />
      )}
    </svg>
  );
}

function SummaryStat({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: StatusTone;
}) {
  const toneCls =
    tone === "good"
      ? "text-good"
      : tone === "warn"
        ? "text-warn"
        : tone === "bad"
          ? "text-bad"
          : "text-foreground";
  return (
    <div className="rounded-md border border-line bg-surface/40 px-3 py-2">
      <div className="eyebrow">{label}</div>
      <div
        className={cn(
          "mt-1 font-display text-2xl font-medium tabular-nums tracking-display-tight",
          toneCls,
        )}
      >
        {value}
      </div>
      {hint && (
        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/45">
          {hint}
        </div>
      )}
    </div>
  );
}

/**
 * Series-status badge. Three buckets, each with a distinct tone from
 * the brass-accent palette:
 *
 *   - `Continuing` → `good` (live green dot, pulsing — "actively
 *      producing new episodes")
 *   - `Ended` → `neutral` (muted, no pulse — "wrapped, no new content
 *      expected")
 *   - `Upcoming` → `info` (blue/yellow tone in the palette — "hasn't
 *      aired yet" / "status unknown")
 *
 * The tone choice mirrors how the Wanted-tab and grab-row chips treat
 * "good vs neutral vs info" elsewhere in the app, so a user scanning
 * the series detail picks up the meaning without re-learning a colour
 * key.
 */
function SeriesStatusChip({ status }: { status: SeriesStatus }) {
  if (status === "Continuing") {
    return (
      <Status variant="chip" tone="good" pulse>
        continuing
      </Status>
    );
  }
  if (status === "Ended") {
    return (
      <Status variant="chip" tone="neutral">
        ended
      </Status>
    );
  }
  return (
    <Status variant="chip" tone="info">
      upcoming
    </Status>
  );
}

/**
 * Footer line under the season list reporting when the per-episode
 * TMDB refresh (issue #68) last walked this series. NULL → "never".
 * Otherwise show "Episodes refreshed Xh ago" (or "Xm" / "Xd" for the
 * cases on either side of an hour). Pure presentation — there's no
 * action attached, just a "we know about this" pulse for the user.
 */
function formatEpisodeRefreshFooter(iso: string | null): string {
  if (!iso) return "Episodes have not been refreshed from TMDB yet.";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) {
    return "Episodes have not been refreshed from TMDB yet.";
  }
  const diffMs = Date.now() - t;
  if (diffMs < 0) return "Episodes refreshed just now.";
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Episodes refreshed just now.";
  if (minutes < 60) return `Episodes refreshed ${minutes}m ago.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `Episodes refreshed ${hours}h ago.`;
  const days = Math.floor(hours / 24);
  return `Episodes refreshed ${days}d ago.`;
}


function rowStateIsActive(s: RowState): boolean {
  return s.kind === "searching" || s.kind === "grabbed" || s.kind === "failed";
}

/** Two-digit zero-padded SxxEyy helper. */
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Issue #65: chip showing the current `series_type` with a click-to-
 * change popover. Three options surfaced as a small radio-button
 * panel; selecting fires the PATCH mutation. No "auto" option here —
 * once a series is in the library the type is a concrete value, and
 * the user explicitly picks. The Add Series modal is where "auto-
 * detect" lives.
 */
function SeriesTypeChip({
  value,
  pending,
  onChange,
}: {
  value: SeriesType;
  pending: boolean;
  onChange: (next: SeriesType) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click. The popover is small and ephemeral; we
  // don't pull in a portal-based menu library for this one chip.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={pending}
        title="Change series type"
        className={cn(
          "focus-ring inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] transition",
          pending
            ? "cursor-progress border-line bg-surface-2/50 text-foreground/55"
            : "border-line bg-surface-2/50 text-foreground/75 hover:border-line-strong hover:text-foreground",
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>type · {SERIES_TYPE_LABEL[value]}</span>
        <svg
          className={cn(
            "h-2.5 w-2.5 text-foreground/55 transition-transform",
            open && "rotate-180",
          )}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          aria-hidden
        >
          <path d="m3 4.5 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Series type"
          className="absolute z-10 mt-1.5 w-40 overflow-hidden rounded-md border border-line bg-surface shadow-elev"
        >
          {SERIES_TYPE_OPTIONS.map((t) => {
            const checked = value === t.value;
            return (
              <button
                key={t.value}
                type="button"
                role="option"
                aria-selected={checked}
                title={t.hint}
                onClick={() => {
                  setOpen(false);
                  if (!checked) onChange(t.value);
                }}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-2 text-left text-xs transition",
                  checked
                    ? "bg-accent/15 text-accent"
                    : "text-foreground/85 hover:bg-surface-2/60",
                )}
              >
                <span className="font-medium">{t.label}</span>
                {checked && (
                  <span className="font-mono text-[10px] opacity-65">
                    current
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Issue #66: per-series "Monitor specials (S00)" toggle. The label
 * sits in the metadata column under the TMDB / IMDB / Path block so
 * it's discoverable but not in the way of the primary series actions.
 *
 * Click flips between true / false. We never offer "clear override" in
 * the UI — having the toggle echo the global default is fine and the
 * server normalises absent overrides to whatever the global says, so
 * a follow-up admin change still lands.
 */
function SpecialsToggleRow({
  value,
  pending,
  onChange,
}: {
  value: boolean;
  pending: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="rounded-md border border-line bg-surface/40 px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-display text-sm font-medium tracking-display-tight">
            Monitor specials
          </div>
          <p className="mt-0.5 text-xs text-foreground/55">
            Season 0 (TVDB / TMDB) holds OVAs, pilots, behind-the-scenes
            shorts and holiday specials. Off by default — flip on for
            anime-OVA chasers and completionists.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange(!value)}
          disabled={pending}
          aria-pressed={value}
          className={cn(
            "focus-ring inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition",
            pending && "cursor-progress opacity-65",
            value
              ? "border-accent/60 bg-accent/40"
              : "border-line bg-surface-2/60",
          )}
          title={
            value
              ? "Stop monitoring specials for this series"
              : "Start monitoring specials for this series"
          }
        >
          <span
            className={cn(
              "h-4 w-4 rounded-full bg-foreground/85 transition-transform",
              value ? "translate-x-6" : "translate-x-1",
            )}
          />
        </button>
      </div>
    </div>
  );
}
